import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import LineChart from '../components/charts/LineChart';
import PieChart from '../components/charts/PieChart';
import BarChart from '../components/charts/BarChart';
import ScatterPlot from '../components/charts/ScatterPlot';
import Card from '../components/ui/Card';
import TabNavigation from '../components/ui/TabNavigation';
import Loading from '../components/ui/Loading';
import Badge from '../components/ui/Badge';
import NoDataMessage from '../components/ui/NoDataMessage';
import PartyBadge from '../components/ui/PartyBadge';
import StatCard from '../components/ui/StatCard';
import { getPartyColor } from '../utils/theme';
import { getCachedData, setCachedData, CACHE_KEYS } from '../utils/cacheUtils';
import { Chart as ChartJS } from 'chart.js';

// Import custom hooks
import useElectorateData from '../hooks/useElectorateData';
import useSimilarElectorates from '../hooks/useSimilarElectorates';
import { markStart, markEnd } from '../utils/performanceUtils';
import { formatCurrency, formatNumber } from '../utils/formatUtils';

const ElectorateDetails = ({ appData }) => {
  console.log('ElectorateDetails component rendering');
  console.log('appData:', appData ? 'present' : 'missing');
  
  if (!appData) {
    console.error('ElectorateDetails: appData is missing');
  } else {
    console.log('ElectorateDetails: appData properties:', Object.keys(appData).join(', '));
    console.log('ElectorateDetails: electorates count:', appData.electorates ? appData.electorates.length : 'missing');
  }
  
  const { name } = useParams();
  console.log('ElectorateDetails: name from useParams:', name);
  
  const [activeTab, setActiveTab] = useState('overview');
  const navigate = useNavigate();
  
  // Find basic electorate info by name
  const basicElectorate = useMemo(() => {
    console.log('ElectorateDetails: Finding basic electorate info for', name);
    if (!appData?.electorates || !appData.electorates.length || !name) {
      console.error('ElectorateDetails: Cannot find basic info - missing data');
      return null;
    }
    const found = appData.electorates.find(e => e.name.toLowerCase() === name.toLowerCase());
    console.log('ElectorateDetails: Found basic info?', found ? 'yes' : 'no');
    return found;
  }, [appData?.electorates, name]);
  
  // Use the custom hook for loading detailed electorate data
  const { 
    data: electorate, 
    loading, 
    error, 
    chartData,
    refresh: refreshElectorateData
  } = useElectorateData(basicElectorate?.name, {
    baseUrl: process.env.PUBLIC_URL || '',
    enableLogging: true // Always enable logging for debugging
  });
  
  console.log('ElectorateDetails: useElectorateData hook returned:', { 
    hasData: !!electorate, 
    loading, 
    hasError: !!error, 
    hasChartData: !!chartData
  });
  
  if (error) {
    console.error('ElectorateDetails: Error loading data:', error);
  }
  
  // Use the custom hook for finding and loading similar electorates
  let similarElectorates = [];
  let similarElectoratesData = [];
  let loadingSimilar = false;
  let similarError = null;
  let refreshSimilarData = () => {};

  try {
    const hookResult = useSimilarElectorates(
      electorate,
      appData?.electorates || [],
      appData?.similarityData || {},
      {
        baseUrl: process.env.PUBLIC_URL || '',
        limit: 5,
        useClassification: true,
        useStateFilter: true
      }
    ) || {};
    
    similarElectorates = hookResult.similarElectorates || [];
    similarElectoratesData = hookResult.detailedData || [];
    loadingSimilar = hookResult.loading || false;
    similarError = hookResult.error || null;
    refreshSimilarData = hookResult.refresh || (() => {});
  } catch (err) {
    console.error('Error initializing useSimilarElectorates hook:', err);
    similarError = err.message || 'Error initializing similar electorates data';
  }

  console.log('ElectorateDetails: useSimilarElectorates hook returned:', { 
    similarElectoratesCount: similarElectorates?.length || 0, 
    similarElectoratesDataCount: similarElectoratesData?.length || 0,
    loadingSimilar,
    hasError: !!similarError
  });
  
  // Format the total for display
  const formattedTotal = useMemo(() => {
    if (!electorate?.totalValue) return 'N/A';
    return formatCurrency(electorate.totalValue, { 
      showSymbol: true,
      abbreviate: electorate.totalValue > 1000000
    });
  }, [electorate?.totalValue]);
  
  // Add tab selection handler
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    // Track tab change performance
    markStart('tab_change');
    setTimeout(() => markEnd('tab_change'), 100);
  };
  
  // Force refresh all data
  const handleRefreshData = () => {
    refreshElectorateData();
    refreshSimilarData();
  };

  // Add a function to prepare demographic comparison data
  const demographicComparisonData = useMemo(() => {
    try {
      if (!electorate || !Array.isArray(similarElectoratesData) || similarElectoratesData.length === 0) {
        console.log('Missing data for demographic comparison:', {
          hasElectorate: !!electorate,
          similarElectoratesDataType: Array.isArray(similarElectoratesData) ? 'array' : typeof similarElectoratesData,
          similarElectoratesDataLength: Array.isArray(similarElectoratesData) ? similarElectoratesData.length : 'N/A'
        });
        return {
          populationData: [],
          fundingPerPersonData: [],
          voterParticipationData: []
        };
      }
      
      // Ensure we have a valid array to work with
      const allElectorates = [electorate].concat(
        Array.isArray(similarElectoratesData) ? similarElectoratesData : []
      ).filter(Boolean);
      
      // Prepare data for population comparison - with defensive checks
      const populationData = allElectorates
        .filter(e => e && typeof e.population === 'number')
        .map(e => ({
          name: e.name || 'Unknown',
          value: e.population,
          formattedValue: formatNumber(e.population)
        }));
      
      // Prepare data for funding per person comparison - with defensive checks
      const fundingPerPersonData = allElectorates
        .filter(e => e && typeof e.population === 'number' && typeof e.totalValue === 'number' && e.population > 0)
        .map(e => ({
          name: e.name || 'Unknown',
          value: e.totalValue / e.population,
          formattedValue: formatCurrency(e.totalValue / e.population)
        }));
      
      // Prepare data for voter participation comparison - with defensive checks
      const voterParticipationData = allElectorates
        .filter(e => e && typeof e.population === 'number' && typeof e.voters === 'number' && e.population > 0)
        .map(e => ({
          name: e.name || 'Unknown',
          value: (e.voters / e.population) * 100,
          formattedValue: `${Math.round((e.voters / e.population) * 100)}%`
        }));
      
      return {
        populationData,
        fundingPerPersonData,
        voterParticipationData
      };
    } catch (err) {
      console.error('Error preparing demographic comparison data:', err);
      // Return safe empty data on error
      return {
        populationData: [],
        fundingPerPersonData: [],
        voterParticipationData: []
      };
    }
  }, [electorate, similarElectoratesData]);

  console.log('Demographic comparison data:', {
    populationDataCount: demographicComparisonData.populationData?.length || 0,
    fundingDataCount: demographicComparisonData.fundingPerPersonData?.length || 0,
    voterDataCount: demographicComparisonData.voterParticipationData?.length || 0
  });

  // Render loading state or error message if needed
  if (loading) {
    return (
      <div className="container mx-auto p-4">
        <Loading size="LARGE" fullScreen />
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto p-4">
        <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 rounded mb-6">
          <p className="font-bold">Error</p>
          <p>{error}</p>
          <button
            onClick={() => navigate('/')} 
            className="mt-4 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
          >
            Return to Home
          </button>
        </div>
      </div>
    );
  }

  if (!electorate) {
    return (
      <div className="container mx-auto p-4">
        <div className="bg-yellow-100 border-l-4 border-yellow-500 text-yellow-700 p-4 rounded mb-6">
          <p className="font-bold">Electorate Not Found</p>
          <p>Could not find an electorate with the name: {name}</p>
          <button
            onClick={() => navigate('/')} 
            className="mt-4 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
          >
            Return to Home
          </button>
        </div>
      </div>
    );
  }
  
  // Continue with your existing JSX for displaying the electorate details
  // ... rest of the component

  return (
    <div className="container mx-auto p-4">
      <header className="mb-12 bg-gray-800 p-8 rounded-2xl shadow-lg border border-gray-700 relative overflow-hidden animate-fadeIn">
        <div className="absolute top-0 right-0 w-full h-full bg-blue-900/10 rounded-br-[100px]"></div>
        <div className="relative z-10 flex flex-col md:flex-row md:items-start md:justify-between">
          <div className="mb-6 md:mb-0">
            <div className="flex items-center mb-2">
              <h1 className="text-3xl md:text-4xl font-bold mr-3 font-sora text-white">{electorate.name}</h1>
              {electorate.party && (
                <PartyBadge party={electorate.party} margin={electorate.margin} />
              )}
            </div>
            <p className="text-lg text-gray-300 mb-2 font-inter flex items-center">
              <span className="inline-block mr-2 text-blue-400">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                </svg>
              </span>
              {electorate.state}
            </p>
            
            {/* Add demographic info to header */}
            {electorate.classification && (
              <p className="text-sm text-gray-400 mb-3 flex items-center">
                <span className="inline-block mr-2 text-blue-400">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                    <path d="M10.707 2.293a1 1 0 00-1.414 0l-7 7a1 1 0 001.414 1.414L4 10.414V17a1 1 0 001 1h2a1 1 0 001-1v-2a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 001 1h2a1 1 0 001-1v-6.586l.293.293a1 1 0 001.414-1.414l-7-7z" />
                  </svg>
                </span>
                {electorate.classification} electorate
              </p>
            )}
            
            <div className="flex flex-wrap gap-3 mt-4">
              <div className="flex items-center bg-gray-700/50 px-3 py-1.5 rounded-lg text-sm">
                <span className="text-gray-300 mr-2">🗳️</span>
                <span className="text-blue-300 font-medium">{electorate.population?.toLocaleString() || 'N/A'}</span>
                <span className="text-gray-400 ml-1">residents</span>
              </div>
              
              <div className="flex items-center bg-gray-700/50 px-3 py-1.5 rounded-lg text-sm">
                <span className="text-gray-300 mr-2">🗺️</span>
                <span className="text-blue-300 font-medium">{electorate.area?.toLocaleString() || 'N/A'}</span>
                <span className="text-gray-400 ml-1">km²</span>
              </div>
              
              {electorate.representative && (
                <div className="flex items-center bg-gray-700/50 px-3 py-1.5 rounded-lg text-sm">
                  <span className="text-gray-300 mr-2">👤</span>
                  <span className="text-blue-300 font-medium">{electorate.representative}</span>
                </div>
              )}
            </div>
          </div>
          
          <div className="bg-gray-750 p-5 rounded-xl border border-gray-700 shadow-lg md:text-right">
            <div className="text-3xl font-bold text-green-400 font-sora mb-2">{formattedTotal}</div>
            <p className="text-sm text-gray-400 font-inter mb-3">Total grant funding</p>
            <div className="h-1 w-20 bg-green-900/30 rounded-full mb-4 md:ml-auto"></div>
            <button 
              onClick={handleRefreshData}
              className="inline-flex items-center text-sm text-blue-400 hover:text-blue-300 transition-colors bg-blue-900/20 px-3 py-2 rounded-lg"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path>
              </svg>
              Refresh Data
            </button>
          </div>
        </div>
      </header>

      {/* Tab navigation using the new component - improved styling */}
      <div className="mb-8 sticky top-20 z-20 bg-gray-900/90 py-2 backdrop-blur-sm border-b border-gray-800">
        <TabNavigation 
          tabs={[
            { id: 'overview', label: 'Financial Overview', icon: '💰' },
            { id: 'demographics', label: 'Demographics & Politics', icon: '📊' }
          ]}
          activeTab={activeTab}
          onTabChange={handleTabChange}
        />
      </div>

      {/* Overview Tab Content */}
      {activeTab === 'overview' && (
        <div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
            <Card title="Yearly Funding" icon={<span className="text-blue-500">📅</span>} accentColor="blue">
              {chartData.yearly && chartData.yearly.length > 0 ? (
                <div className="p-2">
                  <LineChart 
                    data={{
                      labels: chartData.yearly.map(item => item.year),
                      datasets: [{
                        label: '',
                        data: chartData.yearly.map(item => item.value),
                        borderColor: 'rgba(56, 189, 248, 1)',
                        backgroundColor: 'rgba(56, 189, 248, 0.2)',
                        borderWidth: 2,
                        tension: 0.1,
                        pointBackgroundColor: 'rgba(56, 189, 248, 1)',
                        pointBorderColor: '#fff',
                        pointRadius: 5,
                        pointHoverRadius: 7
                      }]
                    }}
                    options={{
                      plugins: {
                        legend: {
                          display: false,
                        },
                        tooltip: {
                          callbacks: {
                            label: (context) => {
                              return `$${context.raw.toLocaleString()}`;
                            }
                          },
                          padding: 12,
                          titleFont: {
                            size: 14,
                            weight: 'bold'
                          },
                          bodyFont: {
                            size: 13,
                            weight: 'medium'
                          }
                        }
                      },
                      scales: {
                        y: {
                          beginAtZero: true,
                          ticks: {
                            callback: (value) => formatCurrency(value, { abbreviate: true }),
                            font: {
                              size: 12,
                              weight: 'medium'
                            }
                          },
                          title: {
                            display: true,
                            text: 'Total Funding Amount',
                            font: {
                              weight: 'bold',
                              size: 13
                            },
                            padding: 10
                          }
                        },
                        x: {
                          title: {
                            display: true,
                            text: 'Year',
                            font: {
                              weight: 'bold',
                              size: 13
                            },
                            padding: 10
                          },
                          ticks: {
                            font: {
                              size: 12,
                              weight: 'medium'
                            }
                          }
                        }
                      }
                    }}
                  />
                  <div className="text-gray-400 text-xs italic mt-3 text-center">
                    Annual grant funding allocated to {electorate.name} electorate
                  </div>
                </div>
              ) : (
                <NoDataMessage message="No yearly funding data available" icon="📅" />
              )}
            </Card>
            
            <Card title="Top Funding Agencies" icon={<span className="text-gray-400">🏢</span>}>
              {chartData.agency && chartData.agency.length > 0 ? (
                <div className="p-2">
                  <PieChart 
                    data={chartData.agency}
                    options={{
                      plugins: {
                        legend: {
                          position: 'right',
                          align: 'center',
                          labels: {
                            boxWidth: 12,
                            padding: 10,
                            font: {
                              size: 12,
                              weight: 'medium'
                            },
                            // Ensure text fits by adding line break if too long and handle undefined
                            generateLabels: function(chart) {
                              const defaultLabels = ChartJS.defaults.plugins.legend.labels.generateLabels(chart);
                              return defaultLabels.map(label => {
                                // Handle undefined labels by replacing with 'Unknown'
                                const labelText = label.text || 'Unknown';
                                return {
                                  ...label,
                                  text: labelText && labelText.length > 20 
                                    ? labelText.substring(0, 18) + '...'
                                    : labelText
                                };
                              });
                            }
                          },
                          // Ensure enough height
                          maxWidth: 160
                        },
                        tooltip: {
                          callbacks: {
                            label: (tooltipItem) => {
                              const item = chartData.agency[tooltipItem.dataIndex];
                              const name = item?.name || 'Unknown';
                              return `${name}: ${formatCurrency(item?.value || 0, { abbreviate: false })}`;
                            }
                          },
                          titleFont: {
                            size: 14,
                            weight: 'bold'
                          },
                          bodyFont: {
                            size: 13,
                            weight: 'medium'
                          }
                        }
                      },
                      // Make the chart slightly smaller to give more space to legend
                      layout: {
                        padding: {
                          left: 0,
                          right: 10,
                          top: 0,
                          bottom: 0
                        }
                      }
                    }}
                  />
                  <div className="text-gray-400 text-xs italic mt-3 text-center">
                    Distribution of funding by government agency
                  </div>
                </div>
              ) : (
                <NoDataMessage message="No agency funding data available" icon="🏢" />
              )}
            </Card>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-md mb-8">
            <h2 className="text-xl font-bold mb-4">Electorate Summary</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="text-sm font-medium text-gray-500">Area</h3>
                <p className="text-lg font-semibold text-gray-900">{electorate.area?.toLocaleString() || 'N/A'} km²</p>
              </div>
              
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="text-sm font-medium text-gray-500">Funding per km²</h3>
                <p className="text-lg font-semibold text-gray-900">
                  {electorate.fundingPerKm2 
                    ? `$${Math.round(electorate.fundingPerKm2).toLocaleString()}`
                    : 'N/A'}
                </p>
              </div>
              
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="text-sm font-medium text-gray-500">Population</h3>
                <p className="text-lg font-semibold text-gray-900">{electorate.population?.toLocaleString() || 'N/A'}</p>
              </div>
              
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="text-sm font-medium text-gray-500">Funding per Resident</h3>
                <p className="text-lg font-semibold text-gray-900">
                  {electorate.fundingPerPerson
                    ? `$${Math.round(electorate.fundingPerPerson).toLocaleString()}`
                    : (electorate.population && electorate.totalValue)
                    ? `$${Math.round(electorate.totalValue / electorate.population).toLocaleString()}`
                    : 'N/A'}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Demographics & Politics Tab Content */}
      {activeTab === 'demographics' && (
        <div className="mb-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <Card title="Electorate Stats">
              <div className="space-y-4">
                <StatCard 
                  title="Population" 
                  value={electorate.population?.toLocaleString() || 'N/A'} 
                  icon="👥"
                />
                
                <StatCard 
                  title="Registered Voters" 
                  value={electorate.voters?.toLocaleString() || 'N/A'} 
                  icon="✓"
                  footer={
                    electorate.voters && electorate.population ? (
                      <div>
                        <div className="flex justify-between text-xs text-gray-600 mb-1">
                          <span>Voter Participation</span>
                          <span>{Math.round((electorate.voters / electorate.population) * 100)}%</span>
                        </div>
                        <div className="bg-gray-200 rounded-full h-2.5 overflow-hidden">
                          <div 
                            className="bg-blue-600 h-2.5 rounded-full" 
                            style={{ width: `${Math.round((electorate.voters / electorate.population) * 100)}%` }}
                          ></div>
                        </div>
                      </div>
                    ) : null
                  }
                />
                
                <StatCard 
                  title="Area" 
                  value={electorate.area ? `${electorate.area?.toLocaleString()} km²` : 'N/A'} 
                  icon="🗺️"
                />
                
                <StatCard 
                  title="Population Density" 
                  value={
                    electorate.population && electorate.area
                      ? `${Math.round(electorate.population / electorate.area).toLocaleString()} people/km²`
                      : 'N/A'
                  } 
                  icon="📊"
                />
                
                <StatCard 
                  title="Classification" 
                  value={electorate.classification || 'N/A'} 
                  icon="🏙️"
                />
              </div>
            </Card>
            
            <div className="bg-white p-6 rounded-lg shadow-md col-span-2">
              <h2 className="text-xl font-bold mb-4">Political Representation</h2>
              
              <div className="mb-6">
                {electorate.representative ? (
                  <div className="bg-blue-50 p-5 rounded-lg border border-blue-100">
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="text-lg font-semibold text-blue-900">{electorate.representative}</h3>
                        <p className="text-sm text-blue-700 mb-3">Current Representative</p>
                        
                        <div className="mt-1">
                          {electorate.party && (
                            <PartyBadge party={electorate.party} margin={electorate.margin} />
                          )}
                        </div>
                      </div>
                      
                      <div className="text-right">
                        {electorate.margin && (
                          <div>
                            <div className="text-3xl font-bold" style={{ color: getPartyColor(electorate.party) }}>
                              {electorate.margin}%
                            </div>
                            <p className="text-sm text-gray-700">Electoral Margin</p>
                            <p className="mt-1 text-xs font-medium" style={{ color: getPartyColor(electorate.party) }}>
                              {electorate.margin < 3
                                ? 'Highly marginal seat'
                                : electorate.margin < 6
                                  ? 'Marginal seat'
                                  : electorate.margin < 10
                                    ? 'Fairly safe seat'
                                    : 'Very safe seat'}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ) : (
                  <p>No representative information available</p>
                )}
              </div>
              
              <h3 className="font-semibold text-gray-700 mb-3">Funding Metrics by Politics</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-green-50 p-4 rounded-lg border border-green-100">
                  <h4 className="text-xs font-medium text-green-700">Total Funding</h4>
                  <p className="text-lg font-semibold text-green-900">{formattedTotal}</p>
                </div>
                
                <div className="bg-green-50 p-4 rounded-lg border border-green-100">
                  <h4 className="text-xs font-medium text-green-700">Funding per Voter</h4>
                  <p className="text-lg font-semibold text-green-900">
                    {electorate.voters && electorate.totalValue
                      ? `$${Math.round(electorate.totalValue / electorate.voters).toLocaleString()}`
                      : 'N/A'}
                  </p>
                </div>
                
                <div className="bg-green-50 p-4 rounded-lg border border-green-100">
                  <h4 className="text-xs font-medium text-green-700">Funding per Resident</h4>
                  <p className="text-lg font-semibold text-green-900">
                    {electorate.fundingPerPerson
                      ? `$${Math.round(electorate.fundingPerPerson).toLocaleString()}`
                      : (electorate.population && electorate.totalValue)
                      ? `$${Math.round(electorate.totalValue / electorate.population).toLocaleString()}`
                      : 'N/A'}
                  </p>
                </div>
                
                <div className="bg-green-50 p-4 rounded-lg border border-green-100">
                  <h4 className="text-xs font-medium text-green-700">Funding per km²</h4>
                  <p className="text-lg font-semibold text-green-900">
                    {electorate.fundingPerKm2 
                      ? `$${Math.round(electorate.fundingPerKm2).toLocaleString()}`
                      : 'N/A'}
                  </p>
                </div>
              </div>
            </div>
          </div>
          
          {/* Add new section for demographic comparisons - Always show even if no similar electorates */}
          <div className="bg-white p-6 rounded-lg shadow-md mb-8">
            <h2 className="text-xl font-bold mb-4">Demographic Insights</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-blue-50 p-5 rounded-lg flex items-start gap-4 border border-blue-100">
                <div className="w-16 h-16 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                  <span className="text-blue-600 text-2xl">
                    {electorate.population && electorate.area
                      ? Math.round(electorate.population / electorate.area) > 1000
                        ? '🏙️'
                        : Math.round(electorate.population / electorate.area) > 100
                          ? '🏘️'
                          : '🏡'
                      : '❓'}
                  </span>
                </div>
                <div>
                  <h3 className="text-lg font-semibold mb-2 text-blue-800">Population Density</h3>
                  <p className="text-2xl font-bold mb-1 text-blue-900">
                    {electorate.population && electorate.area
                      ? `${Math.round(electorate.population / electorate.area).toLocaleString()}`
                      : 'N/A'}
                    <span className="text-base font-normal text-blue-700"> people/km²</span>
                  </p>
                  <p className="text-sm text-blue-700">
                    {electorate.population && electorate.area && (
                      Math.round(electorate.population / electorate.area) > 1000
                        ? 'High density urban area'
                        : Math.round(electorate.population / electorate.area) > 100
                          ? 'Medium density suburban area'
                          : 'Low density rural area'
                    )}
                  </p>
                </div>
              </div>
              
              <div className="bg-purple-50 p-5 rounded-lg flex items-start gap-4 border border-purple-100">
                <div className="w-16 h-16 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
                  <span className="text-purple-600 text-2xl">
                    {electorate.voters && electorate.population
                      ? (electorate.voters / electorate.population) > 0.8
                        ? '🗳️'
                        : (electorate.voters / electorate.population) > 0.7
                          ? '📊'
                          : '📉'
                      : '❓'}
                  </span>
                </div>
                <div>
                  <h3 className="text-lg font-semibold mb-2 text-purple-800">Voter Engagement</h3>
                  <p className="text-2xl font-bold mb-1 text-purple-900">
                    {electorate.voters && electorate.population
                      ? `${Math.round((electorate.voters / electorate.population) * 100)}%`
                      : 'N/A'}
                    <span className="text-base font-normal text-purple-700"> voter participation</span>
                  </p>
                  <p className="text-sm text-purple-700">
                    {electorate.voters && electorate.population && (
                      (electorate.voters / electorate.population) > 0.8
                        ? 'Very high voter engagement'
                        : (electorate.voters / electorate.population) > 0.7
                          ? 'Average voter engagement'
                          : 'Below average voter engagement'
                    )}
                  </p>
                </div>
              </div>

              <div className="bg-green-50 p-5 rounded-lg flex items-start gap-4 border border-green-100">
                <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                  <span className="text-green-600 text-2xl">💰</span>
                </div>
                <div>
                  <h3 className="text-lg font-semibold mb-2 text-green-800">Funding Distribution</h3>
                  <p className="text-2xl font-bold mb-1 text-green-900">
                    {electorate.fundingPerPerson
                      ? `$${Math.round(electorate.fundingPerPerson).toLocaleString()}`
                      : (electorate.population && electorate.totalValue)
                        ? `$${Math.round(electorate.totalValue / electorate.population).toLocaleString()}`
                        : 'N/A'}
                    <span className="text-base font-normal text-green-700"> per resident</span>
                  </p>
                  <p className="text-sm text-green-700">
                    {electorate.classification && 
                      `Typical funding level for a ${electorate.classification.toLowerCase()} electorate`}
                  </p>
                </div>
              </div>
              
              <div className="p-5 rounded-lg flex items-start gap-4 border" 
                   style={{ 
                     backgroundColor: electorate.party === 'ALP' ? '#ffebee' : 
                                     electorate.party === 'LIB' ? '#e3f2fd' : 
                                     electorate.party === 'GRN' ? '#e8f5e9' : 
                                     electorate.party === 'NAT' ? '#f1f8e9' : 
                                     '#eceff1',
                     borderColor: electorate.party === 'ALP' ? '#ffcdd2' : 
                                 electorate.party === 'LIB' ? '#bbdefb' : 
                                 electorate.party === 'GRN' ? '#c8e6c9' : 
                                 electorate.party === 'NAT' ? '#dcedc8' : 
                                 '#cfd8dc'
                   }}>
                <div className="w-16 h-16 rounded-full flex items-center justify-center flex-shrink-0"
                    style={{ 
                      backgroundColor: electorate.party === 'ALP' ? '#ffcdd2' : 
                                     electorate.party === 'LIB' ? '#bbdefb' : 
                                     electorate.party === 'GRN' ? '#c8e6c9' : 
                                     electorate.party === 'NAT' ? '#dcedc8' : 
                                     '#cfd8dc'
                    }}>
                  <span className="text-2xl">
                    {electorate.party === 'ALP' ? '🔴' : 
                      electorate.party === 'LIB' ? '🔵' : 
                      electorate.party === 'GRN' ? '🟢' : 
                      electorate.party === 'NAT' ? '🟢' : 
                      electorate.party === 'IND' ? '⚪' : '⚪'}
                  </span>
                </div>
                <div>
                  <h3 className="text-lg font-semibold mb-2" 
                      style={{ 
                        color: electorate.party === 'ALP' ? '#c62828' : 
                              electorate.party === 'LIB' ? '#1565c0' : 
                              electorate.party === 'GRN' ? '#388e3c' : 
                              electorate.party === 'NAT' ? '#558b2f' : 
                              '#455a64'
                      }}>
                    Political Leaning
                  </h3>
                  <p className="text-2xl font-bold mb-1" style={{ color: getPartyColor(electorate.party) }}>
                    {electorate.margin ? `${electorate.margin}%` : 'N/A'}
                    <span className="text-base font-normal ml-1" 
                          style={{ 
                            color: electorate.party === 'ALP' ? '#d32f2f' : 
                                  electorate.party === 'LIB' ? '#1976d2' : 
                                  electorate.party === 'GRN' ? '#388e3c' : 
                                  electorate.party === 'NAT' ? '#558b2f' : 
                                  '#546e7a'
                          }}>
                      electoral margin
                    </span>
                  </p>
                  <p className="text-sm" 
                    style={{ 
                      color: electorate.party === 'ALP' ? '#d32f2f' : 
                            electorate.party === 'LIB' ? '#1976d2' : 
                            electorate.party === 'GRN' ? '#388e3c' : 
                            electorate.party === 'NAT' ? '#558b2f' : 
                            '#546e7a'
                    }}>
                    {electorate.margin && (
                      electorate.margin < 3
                        ? 'Highly marginal seat'
                        : electorate.margin < 6
                          ? 'Marginal seat'
                          : electorate.margin < 10
                            ? 'Fairly safe seat'
                            : 'Very safe seat'
                    )}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Add comparison section - only if there's similar electorates data */}
          {Array.isArray(similarElectoratesData) && similarElectoratesData.length > 0 && (
            <div className="bg-white p-6 rounded-lg shadow-md mb-8">
              <h2 className="text-xl font-bold mb-4">Comparison with Similar Electorates</h2>
              <p className="mb-4">
                Comparing {electorate?.name || ''} with other {electorate?.classification?.toLowerCase() || ''} electorates:
              </p>
              
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                <div>
                  <h3 className="text-lg font-semibold mb-3">Population Comparison</h3>
                  <div className="h-64">
                    {demographicComparisonData.populationData && demographicComparisonData.populationData.length > 0 ? (
                      <BarChart 
                        data={demographicComparisonData.populationData}
                        color="#3B82F6"
                        dataLabel="Population"
                      />
                    ) : (
                      <NoDataMessage message="No population data available" />
                    )}
                  </div>
                </div>
                
                <div>
                  <h3 className="text-lg font-semibold mb-3">Funding per Resident</h3>
                  <div className="h-64">
                    {demographicComparisonData.fundingPerPersonData && demographicComparisonData.fundingPerPersonData.length > 0 ? (
                      <BarChart 
                        data={demographicComparisonData.fundingPerPersonData}
                        color="#10B981"
                        dataLabel="Funding per Resident"
                      />
                    ) : (
                      <NoDataMessage message="No funding data available" />
                    )}
                  </div>
                </div>
              </div>
              
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-lg font-semibold mb-3">Voter Participation</h3>
                  <div className="h-64">
                    {demographicComparisonData.voterParticipationData && demographicComparisonData.voterParticipationData.length > 0 ? (
                      <BarChart 
                        data={demographicComparisonData.voterParticipationData}
                        color="#8B5CF6"
                        dataLabel="Voter Participation (%)"
                      />
                    ) : (
                      <NoDataMessage message="No voter participation data available" />
                    )}
                  </div>
                </div>
                
                <div>
                  <h3 className="text-lg font-semibold mb-3">Political Representation</h3>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Electorate
                          </th>
                          <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Representative
                          </th>
                          <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Party
                          </th>
                          <th scope="col" className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Margin
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {[electorate, ...similarElectoratesData].map((e) => (
                          <tr key={e.name} className="hover:bg-gray-50">
                            <td className="px-4 py-2 whitespace-nowrap text-sm font-medium text-gray-900">
                              {e.name}
                            </td>
                            <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">
                              {e.representative || 'N/A'}
                            </td>
                            <td className="px-4 py-2 whitespace-nowrap">
                              {e.party ? (
                                <PartyBadge party={e.party} />
                              ) : 'N/A'}
                            </td>
                            <td className="px-4 py-2 whitespace-nowrap text-sm text-right text-gray-900">
                              {e.margin ? `${e.margin}%` : 'N/A'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Helper function to shorten agency names - Used by PieChart component
export function shortenAgencyName(name) {
  if (!name) return 'Unknown';
  
  const abbreviations = {
    'Department of Infrastructure': 'Infrastructure',
    'Department of Infrastructure, Transport': 'Infrastructure & Transport',
    'Department of Health': 'Health',
    'Department of Health and Aged Care': 'Health & Aged Care',
    'Department of Education': 'Education',
    'Department of Social Services': 'Social Services',
    'Department of Climate Change': 'Climate Change',
    'Department of Climate Change, Energy': 'Climate & Energy',
    'Australian Trade and Investment': 'Trade & Investment',
    'Australian Research Council': 'Research Council',
    'National Indigenous Australians Agency': 'Indigenous Australians',
    'National Health and Medical Research Council': 'Health & Medical Research',
    'Department of Industry, Science': 'Industry & Science',
    'Attorney-General\'s Department': 'Attorney-General',
    'Department of the Prime Minister': 'Prime Minister & Cabinet',
    'Department of Agriculture': 'Agriculture',
    'Department of Employment': 'Employment',
    'National Emergency Management': 'Emergency Management',
    'Organ and Tissue Authority': 'Organ & Tissue Authority',
    'Department of Home Affairs': 'Home Affairs',
    'National Disability Insurance': 'NDIS',
    '': 'Unknown' // Handle empty string case
  };
  
  // If name is undefined or null, return 'Unknown'
  if (name === undefined || name === null) return 'Unknown';
  
  // Try direct matching first
  for (const [full, abbr] of Object.entries(abbreviations)) {
    if (name.includes(full)) {
      return abbr;
    }
  }
  
  // For any other department names, use a generic algorithm
  // Extract just the main part after "Department of" or remove any agency specifier after a comma
  if (name.includes('Department of')) {
    const mainPart = name.split('Department of ')[1].split(',')[0];
    return mainPart;
  }
  
  // If it's an agency/authority/commission with a comma in name, take first part
  if (name.includes(',') && (name.includes('Agency') || name.includes('Authority') || name.includes('Commission'))) {
    return name.split(',')[0];
  }
  
  // If still too long, truncate with ellipsis
  return name.length > 25 ? name.substring(0, 22) + '...' : name;
}

export default ElectorateDetails;